import { useState } from "react";
import { Grip, Eye, EyeOff, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { PlaceholderLabelDialog } from "@/components/placeholder-label-dialog";
import type { DragControls } from "framer-motion";
import type { DocumentPage } from "@shared/schema";

interface PageAdminControlsProps {
  page: DocumentPage;
  onToggleVisibility: (pageId: string, currentHidden: boolean) => void;
  onInsertPlaceholder?: (afterDisplayOrder: number, label: string) => void;
  dragControls?: DragControls;
  pagePosition?: number; // Optional: visual position for display
  pageDisplayOrder?: number; // The actual displayOrder of this page
  documentColor?: string;
}

export function PageAdminControls({ 
  page, 
  onToggleVisibility,
  onInsertPlaceholder,
  dragControls,
  pagePosition,
  pageDisplayOrder,
  documentColor
}: PageAdminControlsProps) {
  const { isManagerOrAdmin } = useIsAdmin();
  const [showDialog, setShowDialog] = useState(false);
  
  console.log('🔐 PAGE-ADMIN-CONTROLS rendering for page', page.id);
  console.log('   isManagerOrAdmin:', isManagerOrAdmin);
  console.log('   page.hidden:', page.hidden);
  console.log('   pagePosition:', pagePosition);

  if (!isManagerOrAdmin) {
    console.log('   ❌ Not rendering - user is not admin/manager');
    return null;
  }
  
  console.log('   ✅ Rendering controls');

  return (
    <div className="flex items-center justify-center gap-1 mt-1" onClick={(e) => e.stopPropagation()}>
      {dragControls && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 cursor-grab active:cursor-grabbing hover:bg-accent touch-none"
          data-drag-handle="true"
          onPointerDownCapture={(e) => {
            e.stopPropagation();
            dragControls.start(e);
          }}
          aria-label={`Reorder page ${pagePosition ?? 'N/A'}`}
          data-testid={`button-drag-page-${page.id}`}
        >
          <Grip className="h-3 w-3 text-muted-foreground" />
        </Button>
      )}
      
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 hover:bg-accent"
        onClick={(e) => {
          console.log('👁️ PAGE-ADMIN-CONTROLS Eye button clicked!');
          console.log('   pageId:', page.id);
          console.log('   currentHidden:', page.hidden);
          console.log('   will toggle to:', !page.hidden);
          e.stopPropagation();
          onToggleVisibility(page.id, page.hidden);
        }}
        aria-label={page.hidden ? `Show page ${pagePosition ?? 'N/A'}` : `Hide page ${pagePosition ?? 'N/A'}`}
        aria-pressed={page.hidden}
        data-testid={`button-toggle-visibility-page-${page.id}`}
      >
        {page.hidden ? (
          <EyeOff className="h-3 w-3 text-muted-foreground" />
        ) : (
          <Eye className="h-3 w-3 text-muted-foreground" />
        )}
      </Button>
      
      {onInsertPlaceholder && pageDisplayOrder !== undefined && (
        <>
          <Button
            variant="ghost"
            size="sm"
            className="h-11 w-11 sm:h-8 sm:w-8 p-0 hover:bg-accent"
            onClick={(e) => {
              console.log('➕ PAGE-ADMIN-CONTROLS Add placeholder button clicked!');
              console.log('   pageDisplayOrder:', pageDisplayOrder);
              e.stopPropagation();
              setShowDialog(true);
            }}
            aria-label={`Add placeholder after page ${pagePosition}`}
            data-testid={`button-add-placeholder-after-${page.id}`}
            title="Add placeholder slide after this page"
          >
            <Plus className="h-4 w-4 sm:h-3 sm:w-3 text-muted-foreground" />
          </Button>
          
          <PlaceholderLabelDialog
            open={showDialog}
            onOpenChange={setShowDialog}
            onConfirm={(label) => {
              onInsertPlaceholder(pageDisplayOrder, label);
            }}
            documentColor={documentColor}
          />
        </>
      )}
    </div>
  );
}
